import React from 'react';
import { Container, Box, Typography } from '@mui/material';
import { InfiniteScroll, ZSpinner } from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import RemindersFilters from './reminders-filters';
import { AssigneeOption, Option } from './options';
import ReminderCard from '../../../components/list-cards/reminder-cards';
import RemindersActionBar from './action-bar/reminders-action-bar-container';

type RemindersViewProps = {
  // Data
  tickets: Ticket[];
  totalHits: number;
  // Loading State
  isLoading: boolean;
  error: ApiError | undefined;
  hasMore: boolean;
  loadMore: () => void;
  // Assignee Filter
  assignees: AssigneeOption[];
  selectedAssignee: AssigneeOption;
  onAssigneeChange: (assignee: AssigneeOption | null) => void;
  // Status Filter
  statusOptions: Option[];
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  // Actions
  onNewReminder: () => void;
  onEditTicket: (ticket: Ticket) => void;
  onDuplicateTicket: (ticket: Ticket) => void;
  onRescheduleTicket: (ticket: Ticket) => void;
  onToggleComplete: (ticket: Ticket) => void;
  onDismissTicket: (ticket: Ticket) => void;
  // Action loading states
  reschedulingTicketId?: string;
  togglingCompleteTicketId?: string;
};

const RemindersView = ({
  // Data
  tickets,
  totalHits,
  // Loading State
  isLoading,
  error,
  hasMore,
  loadMore,
  // Assignee Filter
  assignees,
  selectedAssignee,
  onAssigneeChange,
  // Status Filter
  statusOptions,
  selectedStatus,
  onStatusChange,
  // Actions
  onNewReminder,
  onEditTicket,
  onDuplicateTicket,
  onRescheduleTicket,
  onToggleComplete,
  onDismissTicket,
  // Action loading states
  reschedulingTicketId,
  togglingCompleteTicketId,
}: RemindersViewProps) => {
  const formattedCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <Container maxWidth="xl">
      <Box pt={6} pb={12}>
        <Typography variant="h2" component="h1" mb={2}>
          Reminders
        </Typography>
        <RemindersFilters
          // Assignee Props
          assignees={assignees}
          selectedAssignee={selectedAssignee}
          onAssigneeChange={onAssigneeChange}
          // Status Filter Props
          statusOptions={statusOptions}
          selectedStatus={selectedStatus}
          onStatusChange={onStatusChange}
        />
        {!isLoading && tickets.length > 0 && (
          <Typography variant="h4" sx={{ mb: 2 }}>
            {`${formattedCount} Results`}
          </Typography>
        )}
        {isLoading && tickets.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <ZSpinner />
          </Box>
        ) : (
          <InfiniteScroll
            error={error}
            hasMoreItems={hasMore}
            isLoading={isLoading}
            loadMoreItems={loadMore}
            noItemsText="No reminders found."
            stackProps={{ spacing: 1 }}
          >
            {tickets?.map((ticket) => (
              <ReminderCard
                key={ticket.id}
                ticket={ticket}
                assignees={assignees}
                isRescheduling={reschedulingTicketId === ticket.id}
                isTogglingComplete={togglingCompleteTicketId === ticket.id}
                onEdit={() => onEditTicket(ticket)}
                onDuplicate={() => onDuplicateTicket(ticket)}
                onReschedule={() => onRescheduleTicket(ticket)}
                onToggleComplete={() => onToggleComplete(ticket)}
                onDismiss={() => onDismissTicket(ticket)}
              />
            ))}
          </InfiniteScroll>
        )}
      </Box>
      <RemindersActionBar onNewReminder={onNewReminder} />
    </Container>
  );
};

export default RemindersView;
